import { useCurrentImage } from '@/src/composables/useCurrentImage';
import vtkCrosshairsWidget from '@/src/vtk/CrosshairsWidget';
import type { Bounds, Vector3 } from '@kitware/vtk.js/types';
import vtkBoundingBox from '@kitware/vtk.js/Common/DataModel/BoundingBox';
import { computed, ref, unref, watch } from 'vue';
import { vec3 } from 'gl-matrix';
import { defineStore } from 'pinia';
import { Manifest, StateFile } from '@/src/io/state-file/schema';
import { useViewStore } from '@/src/store/views';
import useViewSliceStore from '@/src/store/view-configs/slicing';
import { ViewInfo2D } from '@/src/types/views';
import { computeEffectiveView } from '@/src/core/views/effectiveView';
import { LPSAxis } from '@/src/types/lps';

export const useCrosshairsToolStore = defineStore('crosshairs', () => {
  type _This = ReturnType<typeof useCrosshairsToolStore>;

  const factory = vtkCrosshairsWidget.newInstance();
  const widgetState = factory.getWidgetState();
  const handle = widgetState.getHandle();

  const active = ref(false);
  const { currentImageID, currentImageMetadata } = useCurrentImage('global');

  // world-space
  const position = ref<Vector3>([0, 0, 0]);
  // image space
  const imagePosition = computed(() => {
    const out = vec3.create();
    vec3.transformMat4(
      out,
      position.value,
      currentImageMetadata.value.worldToIndex
    );
    return out as Vector3;
  });

  const viewSliceStore = useViewSliceStore();
  const viewStore = useViewStore();

  const otherViews = computed(() => {
    return viewStore
      .getViewsForData(unref(currentImageID))
      .filter((view): view is ViewInfo2D => view.type === '2D');
  });

  function getWidgetFactory(this: _This) {
    return factory;
  }

  function setPosition(pos: Vector3) {
    position.value = pos;
  }

  // crosshair position -> per-view slice
  watch(imagePosition, (indexPos) => {
    if (!active.value) {
      return;
    }
    const imageID = unref(currentImageID);
    if (!imageID) {
      return;
    }
    const { lpsOrientation } = unref(currentImageMetadata);

    otherViews.value.forEach((view) => {
      const effective = computeEffectiveView(view, imageID);
      if (effective.kind !== 'volume2D') return;
      const index = lpsOrientation[effective.axis];
      const slice = Math.round(indexPos[index]);
      viewSliceStore.updateConfig(view.id, imageID, { slice });
    });
  });

  // per-view slice -> crosshair position (inverse of the watch above).
  // Without this, scrolling a 2D view moves the slice away from the crosshair
  // point and the crosshair lines disappear (CrosshairsTool only renders the
  // overlay when the view's current slice matches the crosshair's slice).
  // The cycle is self-terminating: setting position from a slice produces an
  // index-space value that already equals the slice we just read, so the
  // forward watch above re-applies the same slice and updateConfig early-outs.
  const viewSliceState = computed(() => {
    const imageID = unref(currentImageID);
    if (!imageID) return [];
    return otherViews.value
      .map((view) => {
        const effective = computeEffectiveView(view, imageID);
        if (effective.kind !== 'volume2D') return null;
        const config = viewSliceStore.getConfig(view.id, imageID);
        return {
          axis: effective.axis as LPSAxis,
          slice: config.slice,
        };
      })
      .filter((entry): entry is { axis: LPSAxis; slice: number } => !!entry);
  });

  function syncPositionFromViewSlices() {
    const entries = viewSliceState.value;
    if (entries.length === 0) return;
    const metadata = unref(currentImageMetadata);
    const { lpsOrientation } = metadata;

    const indexPos = vec3.create();
    vec3.transformMat4(indexPos, position.value, metadata.worldToIndex);

    let changed = false;
    entries.forEach(({ axis, slice }) => {
      const axisIndex = lpsOrientation[axis];
      if (Math.round(indexPos[axisIndex]) !== slice) {
        indexPos[axisIndex] = slice;
        changed = true;
      }
    });
    if (!changed) return;

    const newWorld = vec3.create();
    vec3.transformMat4(newWorld, indexPos, metadata.indexToWorld);
    position.value = newWorld as Vector3;
  }

  watch(viewSliceState, () => {
    if (!active.value) return;
    syncPositionFromViewSlices();
  });

  // update widget state based on current image
  watch(
    currentImageMetadata,
    (metadata) => {
      widgetState.setIndexToWorld(metadata.indexToWorld);
      widgetState.setWorldToIndex(metadata.worldToIndex);
      const [xDim, yDim, zDim] = metadata.dimensions;
      const imageBounds: Bounds = [0, xDim - 1, 0, yDim - 1, 0, zDim - 1];
      // inflate by 0.5, since the image slice rendering is inflated
      // by 0.5.
      handle.setBounds(vtkBoundingBox.inflate(imageBounds, 0.5));
    },
    { immediate: true }
  );

  // Initialize the crosshair to the volume center whenever a new image
  // becomes the active one. The default `position` is [0,0,0] (world origin),
  // which usually puts the crosshair in a corner and makes the first
  // activation look like the lines are missing.
  watch(
    currentImageID,
    (newImageID) => {
      if (!newImageID) return;
      const metadata = unref(currentImageMetadata);
      const [xDim, yDim, zDim] = metadata.dimensions;
      const centerIndex: Vector3 = [
        (xDim - 1) / 2,
        (yDim - 1) / 2,
        (zDim - 1) / 2,
      ];
      const centerWorld = vec3.create();
      vec3.transformMat4(centerWorld, centerIndex, metadata.indexToWorld);
      position.value = centerWorld as Vector3;
    },
    { immediate: true }
  );

  // update the position
  handle.onModified(() => {
    const origin = handle.getOrigin();
    if (origin) {
      position.value = origin;
    }
  });

  function activateTool() {
    active.value = true;
    // Snap the crosshair to wherever the 2D views currently are, so the
    // crosshair lines appear immediately in every view instead of only after
    // the user scrolls. Without this, a view that was scrolled away from the
    // middle slice before activation would not show any crosshair line.
    syncPositionFromViewSlices();
    return true;
  }

  function deactivateTool() {
    active.value = false;
    widgetState.setDragging(false);
  }

  function setDragging(dragging: boolean) {
    widgetState.setDragging(dragging);
  }

  function serialize(state: StateFile) {
    const crosshairs = state.manifest.tools?.crosshairs;
    if (!crosshairs) return;
    crosshairs.position = position.value;
  }

  function deserialize(manifest: Manifest) {
    const crosshairsPosition = manifest.tools?.crosshairs?.position;
    if (crosshairsPosition) {
      position.value = crosshairsPosition;
    }
  }

  return {
    getWidgetFactory,
    setPosition,
    position,
    imagePosition,
    activateTool,
    deactivateTool,
    setDragging,
    serialize,
    deserialize,
  };
});
